import { useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";

const sections = [
  {
    id: "data-collected",
    title: "1. Data Pribadi yang Kami Kumpulkan",
    content: [
      {
        subtitle: "A. Data Identitas & Profil",
        items: [
          "Nama lengkap sesuai Kartu Identitas (KTP/Paspor)",
          "Nomor Induk Kependudukan (NIK)",
          "Alamat tempat tinggal (Blok, Nomor Rumah, Kawasan)",
          "Nomor telepon seluler dan/atau nomor WhatsApp",
          "Alamat surat elektronik (email)",
          "Data keluarga (jika didaftarkan dalam satu Kartu Keluarga di aplikasi)",
        ],
      },
      {
        subtitle: "B. Data Keuangan & Transaksi",
        items: [
          "Nomor Rekening BSI dan/atau Virtual Account (VA) yang terafiliasi",
          "Riwayat pembayaran tagihan lingkungan (IPL, Keamanan, Sampah, Air)",
          "Status dan bukti transaksi pembayaran",
        ],
      },
      {
        subtitle: "C. Data Perangkat & Teknis",
        items: [
          "Identitas perangkat (Device ID), jenis perangkat, dan sistem operasi",
          "Data lokasi geografis (jika diizinkan)",
          "Data log aktivitas server demi keamanan sistem perbankan",
        ],
      },
      {
        subtitle: "D. Izin Akses Perangkat",
        items: [
          "Lokasi (GPS) — Digunakan saat fitur Tombol Darurat aktif untuk mengirim koordinat kejadian",
          "Kamera & Galeri — Untuk foto profil, pelaporan kejadian, atau pemindaian QR/QRIS",
          "Notifikasi — Untuk pengiriman tagihan, berita lingkungan, dan status transaksi",
        ],
      },
    ],
  },
  {
    id: "data-usage",
    title: "2. Penggunaan Data Pribadi",
    items: [
      "Penyediaan Layanan: Memproses pembayaran tagihan lingkungan secara otomatis via Virtual Account BSI",
      "Manajemen Lingkungan: Membantu Pengurus dalam tata kelola warga, administrasi digital, dan manajemen tamu",
      "Keamanan & Keselamatan: Memfasilitasi respons cepat dari petugas saat Emergency Button diaktifkan",
      "Komunikasi: Menyampaikan informasi pemeliharaan sistem dan pengumuman penting",
      "Kepatuhan Hukum: Memenuhi kewajiban terhadap regulasi BI, OJK, dan peraturan Indonesia",
    ],
  },
  {
    id: "data-disclosure",
    title: "3. Pengungkapan Data Pribadi",
    items: [
      "Pengurus Lingkungan (RT/RW/Pengelola) — Untuk keperluan administrasi dan transparansi kas",
      "Penyedia Jasa Pihak Ketiga (Mitra Bank) — Terikat kontrak kerahasiaan yang ketat",
      "Otoritas Berwenang — Jika dipersyaratkan hukum, peraturan, atau permintaan resmi aparat",
    ],
    note: "Bank menjamin tidak akan menjual atau menyewakan data pribadi Anda kepada pihak manapun.",
  },
  {
    id: "data-security",
    title: "4. Penyimpanan dan Keamanan Data",
    items: [
      "Standar keamanan perbankan (Banking Grade Security) diterapkan untuk melindungi data Anda",
      "Seluruh transmisi data sensitif dienkripsi menggunakan teknologi SSL/TLS",
    ],
  },
  {
    id: "user-rights",
    title: "5. Hak Anda sebagai Pengguna",
    items: [
      "Meminta akses dan salinan data pribadi yang tersimpan di sistem Bank",
      "Meminta koreksi atau pembaruan data pribadi yang tidak akurat",
      "Mengajukan keluhan terkait perlindungan data pribadi",
      "Menarik kembali persetujuan pemrosesan data (dengan konsekuensi tertentu pada fungsionalitas)",
    ],
  },
  {
    id: "retention",
    title: "6. Masa Retensi Data",
    text: "Kami menyimpan Data Pribadi Anda selama Anda menjadi pengguna aktif Aplikasi BWarga atau nasabah BSI, dan/atau selama periode retensi yang diwajibkan peraturan perundang-undangan (minimal 10 tahun untuk dokumen keuangan).",
  },
];

export default function PrivacyPolicy() {
  const navigate = useNavigate();
  const [activeSection, setActiveSection] = useState("data-collected");
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollTo = (id) => {
    document
      .getElementById(id)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
    setActiveSection(id);
  };

  return (
    <div className="min-h-screen bg-slate-50 font-sans">
      {/* Top bar */}
      <div
        className={`sticky top-0 z-50 transition-all duration-300 ${
          scrolled ? "bg-white/90 backdrop-blur-md shadow-sm" : "bg-white"
        } border-b border-slate-200`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <button
            onClick={() => {
              const saved = sessionStorage.getItem("scrollPosition");
              navigate(-1);
              if (saved) {
                setTimeout(() => {
                  window.scrollTo({
                    top: parseInt(saved),
                    behavior: "instant",
                  });
                }, 50);
              }
            }}
            className="flex items-center gap-2 text-slate-500 hover:text-slate-900 transition-colors text-sm font-medium"
          >
            <svg
              className="w-4 h-4"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Kembali
          </button>
          <span className="text-sm text-slate-400 font-medium tracking-wide uppercase">
            Kebijakan Privasi
          </span>
          <div className="w-16" />
        </div>
      </div>

      {/* Hero */}
      <div className="bg-gradient-to-br from-teal-700 via-teal-600 to-cyan-500 text-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
              <svg
                className="w-5 h-5 text-white"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"
                />
              </svg>
            </div>
            <span className="text-teal-100 text-sm font-semibold tracking-widest uppercase">
              BWarga × BSI
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl font-bold mb-3 tracking-tight">
            Kebijakan Privasi
          </h1>
          <p className="text-teal-100 text-base md:text-lg max-w-2xl leading-relaxed">
            PT Bank Syariah Indonesia Tbk berkomitmen melindungi privasi dan
            data pribadi Anda dalam setiap penggunaan Aplikasi BWarga.
          </p>
          <p className="mt-4 text-teal-200 text-sm">
            Berlaku sejak: 1 Januari 2025
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <div className="flex gap-8 items-start">
          {/* Sidebar nav */}
          <aside className="hidden lg:block w-64 shrink-0 sticky top-24">
            <div className="bg-white rounded-2xl border border-slate-200 p-4 shadow-sm">
              <p className="text-xs font-bold text-slate-400 uppercase tracking-widest mb-3 px-2">
                Daftar Isi
              </p>
              <nav className="space-y-1">
                {sections.map((s) => (
                  <button
                    key={s.id}
                    onClick={() => scrollTo(s.id)}
                    className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-all duration-150 ${
                      activeSection === s.id
                        ? "bg-teal-50 text-teal-700 font-semibold"
                        : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                    }`}
                  >
                    {s.title}
                  </button>
                ))}
                <button
                  onClick={() => scrollTo("contact")}
                  className={`w-full text-left text-sm px-3 py-2 rounded-lg transition-all duration-150 ${
                    activeSection === "contact"
                      ? "bg-teal-50 text-teal-700 font-semibold"
                      : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                  }`}
                >
                  7. Hubungi Kami
                </button>
              </nav>
            </div>
          </aside>

          {/* Main content */}
          <main className="flex-1 min-w-0 space-y-6">
            {sections.map((section) => (
              <section
                key={section.id}
                id={section.id}
                className="bg-white rounded-2xl border border-slate-200 p-6 md:p-8 shadow-sm scroll-mt-24"
                onMouseEnter={() => setActiveSection(section.id)}
              >
                <h2 className="text-lg font-bold text-slate-900 mb-5 pb-3 border-b border-slate-100">
                  {section.title}
                </h2>

                {section.content &&
                  section.content.map((group, gi) => (
                    <div key={gi} className="mb-5 last:mb-0">
                      <h3 className="text-sm font-semibold text-teal-700 mb-2">
                        {group.subtitle}
                      </h3>
                      <ul className="space-y-2">
                        {group.items.map((item, ii) => (
                          <li
                            key={ii}
                            className="flex items-start gap-2.5 text-sm text-slate-600"
                          >
                            <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}

                {section.items && (
                  <ul className="space-y-2">
                    {section.items.map((item, ii) => (
                      <li
                        key={ii}
                        className="flex items-start gap-2.5 text-sm text-slate-600"
                      >
                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-teal-400 shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                )}

                {section.note && (
                  <div className="mt-4 bg-teal-50 border border-teal-100 rounded-xl p-3.5 text-sm text-teal-800 flex items-start gap-2">
                    <svg
                      className="w-4 h-4 mt-0.5 shrink-0 text-teal-500"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    {section.note}
                  </div>
                )}

                {section.text && (
                  <p className="text-sm text-slate-600 leading-relaxed">
                    {section.text}
                  </p>
                )}
              </section>
            ))}

            {/* Contact */}
            <section
              id="contact"
              className="bg-gradient-to-br from-slate-800 to-slate-900 rounded-2xl p-6 md:p-8 text-white shadow-sm scroll-mt-24"
              onMouseEnter={() => setActiveSection("contact")}
            >
              <h2 className="text-lg font-bold mb-5 pb-3 border-b border-white/10">
                7. Hubungi Kami
              </h2>
              <p className="text-slate-300 text-sm mb-5">
                Jika Anda memiliki pertanyaan, komentar, atau keluhan mengenai
                Kebijakan Privasi ini, silakan hubungi kami:
              </p>
              <div className="grid sm:grid-cols-2 gap-3">
                {[
                  {
                    icon: "🏢",
                    label: "Kantor Pusat",
                    value:
                      "Gedung The Tower, Jl. Gatot Subroto No. 27, Jakarta Selatan 12930",
                  },
                  { icon: "📞", label: "BSI Call", value: "14040" },
                  {
                    icon: "💬",
                    label: "WhatsApp Resmi",
                    value: "0815-8411-4040",
                  },
                  {
                    icon: "✉️",
                    label: "Email",
                    value: "contactus@bankbsi.co.id",
                  },
                ].map((c) => (
                  <div key={c.label} className="bg-white/10 rounded-xl p-4">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-base">{c.icon}</span>
                      <span className="text-xs text-slate-400 font-medium uppercase tracking-wide">
                        {c.label}
                      </span>
                    </div>
                    <p className="text-sm text-white font-medium">{c.value}</p>
                  </div>
                ))}
              </div>
            </section>

            <p className="text-center text-xs text-slate-400 pb-4">
              © 2025 PT Bank Syariah Indonesia Tbk. Semua hak dilindungi.
            </p>
          </main>
        </div>
      </div>
    </div>
  );
}
